import React, { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  getUpdateDynamicSwitchGroupList,
  updatedDynamicSwitchGroupListState,
  resetError,
} from "./UpdatedDynamicSwitchGroupList.action";
import { showNotification } from "services/NotificationService";

import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Tip from "components/Tip";
import CodexVersionDisplayBar from "components/CodexVersionDisplayBar";

import "./UpdatedDynamicSwitchGroupDetails.css";
import {
  DELTA_CLASS_NAME,
  BooleanDeltaField,
  DescriptionDeltaField,
} from "../../../commons/fields/DeltaFieldUtils";
import DrugDeltaIcon from "components/DynamicSwitchGroup/DrugDeltaIcon";
import ClientSideReactTable from "commons/table/ClientSideReactTable";
import { FilterTypes, ColumnTypes } from "commons/table/Constants";
import { WrapTextOnNoSpace } from "components/WrapText";

const DEFAULT_PAGE_SIZE = 15;

const UpdatedDynamicSwitchGroupList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { data, loading, listState, errorFetchingList, codexVersionId } =
    useSelector((state) => {
      const ls =
        state.deltaDynamicSwitchGroupList.listState !== undefined
          ? state.deltaDynamicSwitchGroupList.listState
          : {
              page: 0,
              pageSize: DEFAULT_PAGE_SIZE,
              sorted: state.sorted,
              filtered: [],
              resized: [],
              expanded: {},
            };

      return {
        data:
          state.deltaDynamicSwitchGroupList.data !== undefined
            ? state.deltaDynamicSwitchGroupList.data
            : [],
        loading: state.deltaDynamicSwitchGroupList.loading,
        listState: ls,
        errorFetchingList: state.deltaDynamicSwitchGroupList.errorFetchingList,
        codexVersionId: state.codexVersion
          ? state.codexVersion.codexVersionId
          : null,
      };
    });

  const totalElements = data?.length;

  const getBgColor = useCallback((rowInfo) => {
    return rowInfo.original["currentDynamicSwitchGroup.isDeleted"]
      ? "#FEBDB1"
      : !rowInfo.original["oldDynamicSwitchGroup"]
      ? "#CDFFCD"
      : "";
  }, []);

  const getData = useCallback((row, key1, key2) => {
    return row.original[key1]
      ? row.original[key1][key2]
        ? row.original[key1][key2]
        : null
      : null;
  }, []);

  const renderIcon = useCallback(
    (rowOriginal, isAdded, isDeleted) => {
      const className = isDeleted
        ? DELTA_CLASS_NAME.DELETED
        : isAdded
        ? DELTA_CLASS_NAME.NEW
        : DELTA_CLASS_NAME.MODIFIED;

      return <DrugDeltaIcon showDelta={true} className={className} />;
    },
    []
  );

  const renderCell = useCallback((current, predecessor, isUpdated) => {
    return (
      <DescriptionDeltaField
        newField={current}
        oldField={predecessor}
        isUpdated={isUpdated}
      />
    );
  }, []);

  const renderBooleanCell = useCallback((current, predecessor, isUpdated) => {
    return (
      <BooleanDeltaField
        newField={current}
        oldField={predecessor}
        isUpdated={isUpdated}
      />
    );
  }, []);

  const updateData = useCallback(
    (state, preventUpdate) => {
      if (!preventUpdate && codexVersionId) {
        const { pageSize, page, sorted, filtered } = state;
        dispatch(
          getUpdateDynamicSwitchGroupList(
            pageSize,
            page,
            sorted,
            filtered,
            codexVersionId
          )
        );
      } else if (!codexVersionId) {
        showNotification({
          title: "Oops!",
          message: "You haven't selected a Codex Version to work on.",
          position: "bl",
          type: "error",
          autoDismiss: 4,
        });
        navigate("/cat");
      }

      dispatch(updatedDynamicSwitchGroupListState(state));
    },
    [codexVersionId, dispatch, history]
  );

  const onUpdateTable = useCallback(
    (filterValues, sortBy, pageSize, pageNo) => {
      const nextListState = {
        ...listState,
        pageSize,
        page: pageNo,
        sorted: sortBy ? sortBy : [],
        filtered: filterValues,
      };
      updateData(nextListState);
    },
    [listState, updateData]
  );

  // Initial load – mimic componentDidMount
  useEffect(() => {
    updateData(listState);
  }, []); // intentionally run only once on mount to match previous behavior

  // Error handling – mimic getDerivedStateFromProps error branch
  useEffect(() => {
    if (errorFetchingList) {
      showNotification({
        title: "Error Loading Data!",
        message: "You have entered invalid information.",
        position: "bl",
        type: "error",
      });
      dispatch(resetError());
    }
  }, [errorFetchingList, dispatch]);

  const columns = useMemo(
    () => [
      {
        header: "",
        accessor: "currentDynamicSwitchGroup.isDeleted",
        accessorKey: "currentDynamicSwitchGroup.isDeleted",
        width: 50,
        show: false,
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        header: "",
        accessor: "oldDynamicSwitchGroup.dynamicSwitchGroupName",
        accessorKey: "oldDynamicSwitchGroup.dynamicSwitchGroupName",
        width: 50,
        show: false,
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        header: "",
        accessor: "oldDynamicSwitchGroup.isActive",
        accessorKey: "oldDynamicSwitchGroup.isActive",
        width: 50,
        show: false,
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        header: "",
        accessor: "oldDynamicSwitchGroup.projectedSuccessRate",
        accessorKey: "oldDynamicSwitchGroup.projectedSuccessRate",
        width: 50,
        show: false,
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        header: "",
        accessor: "oldDynamicSwitchGroup.pharmacistNotes",
        accessorKey: "oldDynamicSwitchGroup.pharmacistNotes",
        width: 50,
        show: false,
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        header: "",
        accessor: "currentDynamicSwitchGroup",
        accessorKey: "currentDynamicSwitchGroup",
        width: 50,
        show: false,
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        header: "",
        accessor: "oldDynamicSwitchGroup",
        accessorKey: "oldDynamicSwitchGroup",
        width: 50,
        show: false,
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        header: "",
        accessor: "currentDynamicSwitchGroup.dynamicSwitchGroupId",
        accessorKey: "type",
        width: 40,
        enableSorting: false,
        enableColumnFilter: false,
        columnType: ColumnTypes.Custom,
        customComponent: (value, row) =>
          renderIcon(
            row.original,
            !row.original["oldDynamicSwitchGroup"],
            getData(row, "currentDynamicSwitchGroup", "isDeleted")
          ),
      },
      {
        header: "Switch Group Name",
        accessor: "currentDynamicSwitchGroup.dynamicSwitchGroupName",
        accessorKey: "currentDynamicSwitchGroup.dynamicSwitchGroupName",
        width: 400,
        enableSorting: true,
        enableColumnFilter: true,
        columnType: ColumnTypes.Custom,
        filterType: FilterTypes.SearchableInput,
        customComponent: (value, row) =>
          renderCell(
            getData(row, "currentDynamicSwitchGroup", "dynamicSwitchGroupName"),
            getData(row, "oldDynamicSwitchGroup", "dynamicSwitchGroupName"),
            !getData(row, "currentDynamicSwitchGroup", "isDeleted") &&
              row.original["oldDynamicSwitchGroup"]
          ),
      },
      {
        header: "Class Name",
        accessor: "currentDynamicSwitchGroup.dynamicSwitchGroupClassName",
        accessorKey: "currentDynamicSwitchGroup.dynamicSwitchGroupClassName",
        width: 200,
        show: false,
        enableSorting: true,
        enableColumnFilter: true,
        columnType: ColumnTypes.Custom,
        filterType: FilterTypes.SearchableInput,
        customComponent: (value, row) =>
          renderCell(
            getData(
              row,
              "currentDynamicSwitchGroup",
              "dynamicSwitchGroupClassName"
            ),
            getData(
              row,
              "oldDynamicSwitchGroup",
              "dynamicSwitchGroupClassName"
            ),
            !getData(row, "currentDynamicSwitchGroup", "isDeleted") &&
              row.original["oldDynamicSwitchGroup"]
          ),
      },
      {
        header: "Active",
        accessor: "currentDynamicSwitchGroup.isActive",
        accessorKey: "currentDynamicSwitchGroup.isActive",
        width: 100,
        enableSorting: false,
        enableColumnFilter: false,
        columnType: ColumnTypes.Custom,
        customComponent: (value, row) =>
          renderBooleanCell(
            getData(row, "currentDynamicSwitchGroup", "isActive"),
            getData(row, "oldDynamicSwitchGroup", "isActive"),
            !getData(row, "currentDynamicSwitchGroup", "isDeleted") &&
              row.original["oldDynamicSwitchGroup"]
          ),
      },
      {
        header: "Projected Success Rate",
        accessor: "currentDynamicSwitchGroup.projectedSuccessRate",
        accessorKey: "currentDynamicSwitchGroup.projectedSuccessRate",
        width: 200,
        enableSorting: false,
        enableColumnFilter: false,
        columnType: ColumnTypes.Custom,
        customComponent: (value, row) =>
          renderCell(
            getData(row, "currentDynamicSwitchGroup", "projectedSuccessRate"),
            getData(row, "oldDynamicSwitchGroup", "projectedSuccessRate"),
            !getData(row, "currentDynamicSwitchGroup", "isDeleted") &&
              row.original["oldDynamicSwitchGroup"]
          ),
      },
      {
        header: "Pharmacist Notes",
        accessor: "currentDynamicSwitchGroup.pharmacistNotes",
        accessorKey: "currentDynamicSwitchGroup.pharmacistNotes",
        width: 200,
        enableSorting: false,
        enableColumnFilter: false,
        columnType: ColumnTypes.Custom,
        customComponent: (value, row) =>
          renderCell(
            getData(row, "currentDynamicSwitchGroup", "pharmacistNotes"),
            getData(row, "oldDynamicSwitchGroup", "pharmacistNotes"),
            !getData(row, "currentDynamicSwitchGroup", "isDeleted") &&
              row.original["oldDynamicSwitchGroup"]
          ),
      },
    ],
    [getData, renderBooleanCell, renderCell, renderIcon]
  );

  const handleRowClick = useCallback(
    (rowInfo) => {
      if (rowInfo !== undefined) {
        navigate(
          `/cat/codex-version/${codexVersionId}/dynamic-switch-groups/${rowInfo.original.currentDynamicSwitchGroup.dynamicSwitchGroupId}`
        );
      }
    },
    [navigate, codexVersionId]
  );

  const trStyle = useCallback(
    (rowInfo) => {
      if (rowInfo) {
        return {
          background: getBgColor(rowInfo),
        };
      }
      return {};
    },
    [getBgColor]
  );

  return (
    <Container className="list-container" fluid>
      <CodexVersionDisplayBar />
      <Row className="tiny-bottom-spacer">
        <Col md={6}>
          Total Items:{" "}
          <strong id="sllist-total-count">
            {totalElements !== undefined && !loading
              ? totalElements
              : "Loading ..."}
          </strong>
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <ClientSideReactTable
            columnDefination={columns}
            data={data}
            currentPage={listState.page}
            sorted={listState.sorted}
            filters={listState.filtered}
            totalRecords={totalElements == "loading" ? 0 : totalElements}
            loading={loading}
            enableMultiRowSelection={false}
            addRowSelection={false}
            defaultPageSize={DEFAULT_PAGE_SIZE}
            pageSizeOptions={[5, 10, 15, 20, 30, 50]}
            onUpdateTableData={onUpdateTable}
            overrideRowId={"currentDynamicSwitchGroup.dynamicSwitchGroupId"}
            onRowClick={handleRowClick}
            trStyle={trStyle}
          />
          <Tip>
            Tip: Hold shift when sorting to multi-sort and disable sort!
          </Tip>
        </Col>
      </Row>
    </Container>
  );
};

export default UpdatedDynamicSwitchGroupList;